/**
 * @fileoverview Table component - Molecular UI element
 * 
 * A flexible table component for displaying tabular data.
 * Built with Tailwind CSS for styling.
 * 
 * @module ui/shared/molecules/Table
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../utils/cn";
import { Spinner } from "../atoms/Loading";
import {
  PAGINATION_NEXT,
  PAGINATION_PREV,
  PAGINATION_ROOT,
  PAGINATION_SELECT,
  PAGINATION_TOTAL,
} from "../paginationStyles";

/**
 * Table component props
 */
export interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  /**
   * Whether the table is in loading state
   */
  loading?: boolean;
  /**
   * Table data source
   */
  dataSource?: any[];
  /**
   * Table columns configuration
   */
  columns?: TableColumn[];
  /**
   * Row key field name
   */
  rowKey?: string | ((record: any) => string);
  /**
   * Custom row className function
   */
  rowClassName?: (record: any, index: number) => string;
  /**
   * Pagination configuration
   */
  pagination?: {
    pageSize?: number;
    current?: number;
    total?: number;
    showSizeChanger?: boolean;
    /** The range argument is optional, which is how call sites use it. */
    showTotal?: (total: number, range?: string) => string;
    onChange?: (page: number, pageSize: number) => void;
    onShowSizeChange?: (current: number, size: number) => void;
  };
  /**
   * Scroll configuration
   */
  scroll?: {
    x?: number | string;
    y?: number | string;
  };
}

/**
 * Table column definition
 */
export interface TableColumn {
  /**
   * Column title
   */
  title: string | React.ReactNode;
  /**
   * Data field name
   */
  dataIndex?: string;
  /**
   * Unique key for the column
   */
  key?: string;
  /**
   * Custom render function
   */
  render?: (value: any, record: any, index: number) => React.ReactNode;
  /**
   * Column width
   */
  width?: number | string;
  /**
   * Column alignment
   */
  align?: "left" | "center" | "right";
  /**
   * Whether column is fixed
   */
  fixed?: "left" | "right";
  /**
   * Whether text should be ellipsis
   */
  ellipsis?: boolean;
}

/**
 * Table component
 * 
 * A flexible table component for displaying tabular data with pagination and loading states.
 * 
 * @param {TableProps} props - Table props
 * @returns {JSX.Element} Table element
 * 
 * @example
 * <Table
 *   columns={columns}
 *   dataSource={data}
 *   loading={loading}
 *   rowKey="id"
 * />
 */
export const Table = React.forwardRef<HTMLTableElement, TableProps>(
  ({ 
    className, 
    loading, 
    dataSource = [], 
    columns = [], 
    rowKey = "id",
    rowClassName,
    pagination,
    scroll,
    ...props 
  }, ref) => {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(pagination?.pageSize || 10);

    const getRowKey = React.useCallback((record: any, index: number) => {
      if (typeof rowKey === "function") {
        return rowKey(record);
      }
      return record[rowKey] || index;
    }, [rowKey]);

    const paginatedData = React.useMemo(() => {
      if (!pagination) return dataSource;
      const start = (currentPage - 1) * pageSize;
      const end = start + pageSize;
      return dataSource.slice(start, end);
    }, [dataSource, currentPage, pageSize, pagination]);

    const totalPages = React.useMemo(() => {
      if (!pagination) return 1;
      return Math.ceil(dataSource.length / pageSize);
    }, [dataSource.length, pageSize, pagination]);

    return (
      <div className={cn("ui-table-wrapper relative", className)}>
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-[var(--background)]/80">
            <Spinner size="lg" />
          </div>
        )}
        <div className={cn(scroll?.x ? "overflow-x-auto" : "", "w-full")} style={scroll?.x ? { maxWidth: scroll?.x } : {}}>
          <table
            ref={ref}
            className={cn(
              "ui-table-table w-full border-collapse text-sm text-[var(--foreground)]",
              "table-fixed"
            )}
            style={{ width: "100%", tableLayout: "fixed" }}
            {...props}
          >
            <thead className="ui-table-thead">
              <tr className="border-b border-[var(--border)]">
                {columns.map((column, index) => (
                  <th
                    key={column.key || column.dataIndex || index}
                    className={cn(
                      "ui-table-cell h-10 px-2 text-left align-middle font-medium text-[var(--muted-foreground)]",
                      column.align === "center" && "text-center",
                      column.align === "right" && "text-right",
                      column.fixed === "left" && "sticky left-0 z-10 bg-[var(--card)]",
                      column.fixed === "right" && "sticky right-0 z-10 bg-[var(--card)]"
                    )}
                    style={{ width: column.width }}
                  >
                    {column.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="ui-table-tbody">
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="p-2 py-8 text-center text-sm text-[var(--muted-foreground)]">
                    No data available
                  </td>
                </tr>
              ) : (
                paginatedData.map((record, index) => {
                  const key = getRowKey(record, index);
                  const customClassName = rowClassName ? rowClassName(record, index) : "";
                  return (
                    <tr
                      key={key}
                      className={cn(
                        "ui-table-row border-b border-[var(--border)] transition-colors",
                        customClassName
                      )}
                    >
                      {columns.map((column, colIndex) => {
                        const value = column.dataIndex
                          ? record[column.dataIndex]
                          : undefined;
                        const content = column.render
                          ? column.render(value, record, index)
                          : value;

                        return (
                          <td
                            key={column.key || column.dataIndex || colIndex}
                            className={cn(
                              "ui-table-cell p-2 align-middle text-sm",
                              column.align === "center" && "text-center",
                              column.align === "right" && "text-right",
                              column.fixed === "left" && "sticky left-0 z-10 bg-[var(--card)]",
                              column.fixed === "right" && "sticky right-0 z-10 bg-[var(--card)]",
                              column.ellipsis && "max-w-xs truncate"
                            )}
                            style={{ width: column.width }}
                          >
                            {column.align === "center" ? (
                              <div className="flex items-center justify-center">
                                {content}
                              </div>
                            ) : column.align === "right" ? (
                              <div className="flex items-center justify-end">
                                {content}
                              </div>
                            ) : (
                              <div className="flex items-center">
                                {content}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {pagination && dataSource.length > 0 && (
          <div className="ui-table-pagination mt-4 flex items-center justify-between">
            <div className={cn(PAGINATION_TOTAL, "tabular-nums")}>
              {pagination.showTotal
                ? (() => {
                    const start = (currentPage - 1) * pageSize + 1;
                    const end = Math.min(currentPage * pageSize, dataSource.length);
                    const range = `${start}-${end}`;
                    if (typeof pagination.showTotal === 'function') {
                      try {
                        return pagination.showTotal(dataSource.length, range);
                      } catch {
                        return pagination.showTotal(dataSource.length);
                      }
                    }
                    return pagination.showTotal;
                  })()
                : t("common.totalCount", { count: dataSource.length })}
            </div>
            <nav className={PAGINATION_ROOT} aria-label={t("common.pagination")}>
              <button
                type="button"
                onClick={() => {
                  if (currentPage > 1) {
                    const newPage = currentPage - 1;
                    setCurrentPage(newPage);
                    pagination.onChange?.(newPage, pageSize);
                  }
                }}
                disabled={currentPage === 1}
                className={cn(PAGINATION_PREV, "min-w-0 px-3")}
                aria-label={t("common.previousPage")}
                tabIndex={0}
              >
                {t("common.previous")}
              </button>
              <span className="px-2 text-sm tabular-nums text-[var(--muted-foreground)]">
                {t("common.pageOf", { current: currentPage, total: totalPages })}
              </span>
              <button
                type="button"
                onClick={() => {
                  if (currentPage < totalPages) {
                    const newPage = currentPage + 1;
                    setCurrentPage(newPage);
                    pagination.onChange?.(newPage, pageSize);
                  }
                }}
                disabled={currentPage === totalPages}
                className={cn(PAGINATION_NEXT, "min-w-0 px-3")}
                aria-label={t("common.nextPage")}
                tabIndex={0}
              >
                {t("common.next")}
              </button>
              {pagination.showSizeChanger && (
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setPageSize(newSize);
                    setCurrentPage(1);
                    pagination.onShowSizeChange?.(1, newSize);
                  }}
                  className={cn(PAGINATION_SELECT, "ml-2")}
                  aria-label={t("common.itemsPerPage")}
                >
                  <option value={10}>{t("common.pageSizeOption", { count: 10 })}</option>
                  <option value={20}>{t("common.pageSizeOption", { count: 20 })}</option>
                  <option value={50}>{t("common.pageSizeOption", { count: 50 })}</option>
                  <option value={100}>{t("common.pageSizeOption", { count: 100 })}</option>
                </select>
              )}
            </nav>
          </div>
        )}
      </div>
    );
  }
);

Table.displayName = "Table";

